import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { DatabaseZap, Globe2, LockKeyhole, RadioTower } from 'lucide-react';

const principles = [
  {
    icon: Globe2,
    title: 'How VPN access works',
    copy: 'All VPN traffic is routed strictly through powerful, high speed RAM only servers. There are no accounts and no subscriptions. No browsing activity, traffic content or message content is written to the PhantomPath customer database. Every access code is stateless and exists only for the duration of the session.',
  },
  {
    icon: RadioTower,
    title: 'How calls, messages and eSIMs work',
    copy: 'Our Virtual Numbers and eSIM infrastructure operate on a strict 30 second server side deletion policy. Message and call payloads processed by PhantomPath are automatically deleted after serving their purpose. Limited provisioning, payment, security and compliance records remain operationally separate from communications content.',
  },
  {
    icon: LockKeyhole,
    title: "What we retain — and what we don't",
    copy: (
      <>
        <p>Standard messaging is protected using TLS, and voice traffic is protected using SRTP during transport.</p>
        <p>We utilise an Immediate Deletion API with Telnyx so that message text and call audio are immediately deleted after processing and are never stored on our databases.</p>
      </>
    ),
  },
  {
    icon: DatabaseZap,
    title: 'US Number Verification',
    copy: (
      <>
        <p>The number input field exists only on supported USA numbers to satisfy telecom compliance and verify that a real human is requesting the service.</p>
        <p>Verification information is used only where required for compliance, activation and service protection, and is not used for marketing.</p>
      </>
    ),
  },
];

const ZeroLogManifesto = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Technical Manifesto | PhantomPathVPN</title>
        <meta name="description" content="How PhantomPathVPN provides temporary connectivity while limiting the information collected to operate, secure and support each service." />
        <link rel="canonical" href="https://phantompathvpn.com/zero-log-manifesto" />
      </Helmet>

      <main className="relative min-h-screen overflow-hidden bg-black px-4 pb-24 pt-24 text-gray-100 sm:pt-28">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,200,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,200,0.055)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_75%_65%_at_50%_25%,#000_35%,transparent_100%)]" />
          <div className="absolute left-1/2 top-20 h-96 w-96 -translate-x-1/2 rounded-full bg-[#00ffc8]/[0.055] blur-[110px]" />
        </div>

        <article className="relative mx-auto max-w-3xl">
          <header className="mb-10 text-center sm:mb-12">
            <p className="mb-4 font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-[#00ffc8] sm:text-[11px]">The technical promise</p>
            <h1 className="font-black uppercase leading-none tracking-[0.06em]">
              <span className="block text-lg text-white sm:text-xl md:text-2xl">PhantomPath</span>
              <span className="mt-2 block text-xl text-[#00ffc8] sm:text-2xl md:text-3xl">Technical Manifesto</span>
            </h1>
          </header>

          <div className="border-l border-[#00ffc8]/20 pl-5 sm:pl-7">
            <p className="max-w-2xl pb-10 text-[15px] font-medium leading-7 text-gray-100 sm:pb-12 sm:text-[17px] sm:leading-8">
              PhantomPath is designed to provide temporary connectivity while collecting only the information needed to deliver, secure and support each service. This page explains how that works.
            </p>

            <div className="divide-y divide-white/[0.08]" aria-label="PhantomPath technical privacy principles">
              {principles.map(({ icon: Icon, title, copy }) => (
                <section key={title} className="py-7 first:pt-0 sm:py-8">
                  <h2 className="flex items-center gap-3 text-base font-bold text-white sm:text-lg">
                    <Icon className="h-5 w-5 shrink-0 text-[#00ffc8]" aria-hidden="true" />
                    <span>{title}</span>
                  </h2>
                  <div className="mt-3 space-y-3 text-[13px] leading-6 text-gray-100 sm:text-sm sm:leading-7">{copy}</div>
                </section>
              ))}
            </div>

            <section className="border-t border-white/[0.08] py-9 sm:py-11">
              <div className="space-y-2 text-lg font-bold leading-tight text-white sm:text-xl">
                <p>For VPN access, we do not require a traditional account, email address or phone number.</p>
                <p>Some telecom services require limited information where needed for provider, compliance or fraud-prevention requirements.</p>
              </div>

              <p className="mt-8 text-xs text-gray-100 sm:text-sm">Our philosophy has always been simple:</p>
              <p className="mt-3 text-base font-semibold leading-7 text-gray-100 sm:text-lg sm:leading-8">
                Collect nothing we do not need. Use only the limited information required to provide, secure and support the service and meet applicable obligations.
              </p>

              <div className="mt-9 font-mono text-xs font-bold uppercase tracking-[0.13em] text-[#00ffc8] sm:text-sm">
                <p>Privacy isn't a feature.</p>
                <p className="mt-2">It's the foundation PhantomPath was built on.</p>
              </div>
            </section>
          </div>
        </article>
      </main>
    </>
  );
};

export default ZeroLogManifesto;
